import Decimal from 'decimal.js';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { IFieldConfig, IFooterRow } from './report';
import { add, displayMoney, minus, Money } from '../../../utils/money';
import {
  compute_total_cod,
  compute_total_delivery_fee,
  compute_total_driver_commission,
  compute_total_driver_commission_from_delivery_tasks,
  compute_total_pickup_commission,
  compute_total_pop,
  compute_total_price,
} from './utils';
import { get_cod } from '../../../utils/cod';
import { TotalAmounts } from '../../../interfaces/common/invoice';
import { format } from 'date-fns';
import { IOrderDeliveryTask, IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { Workflow } from '../../../enums/orders';
import { displayLocation } from '../../../utils/locations';
import { OrderDeliveryTaskState } from '../../../enums/tasks';

export const defaultOrgReportFieldsConfig: IFieldConfig<OrderType>[] = [
  {
    display_name: ['Order Id'],
    width_percentage: '10%',
    get_values: (order: OrderType) => [order.order_id],
    get_aggregate: (orders: OrderType[]) => [
      'Total',
      `${orders.length} orders`,
    ],
  },
  {
    display_name: ['Ref #'],
    width_percentage: '10%',
    get_values: (order: OrderType) => [order.reference_id ]
  },
  {
    display_name: ['Customer'],
    width_percentage: '23%',
    get_values: (order: OrderType) => [
      order.customer?.name || 'unknown',
    ],
  },
  {
    display_name: ['Customer Phone'],
    width_percentage: '15%',
    get_values: (order: OrderType) => [order.customer?.phone_number || ''],
  },
   {
    display_name: ['Location'],
    width_percentage: '15%',
    get_values: (order: OrderType) => [order.customer_location?.area?.name_en || ''],
  },
  {
    display_name: ['Merchant'],
    width_percentage: '23%',
    get_values: (order: OrderType) => [order.merchant?.name  ?? 'ERROR'],
  },
  {
    display_name: ['COD'],
    width_percentage: '15%',
    get_values: (order: OrderType) => displayMoney(get_cod(order)).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_cod(orders)).split(','),
  },
  {
    display_name: ['Del. Fee'],
    width_percentage: '15%',
    get_values: (order: OrderType) =>
      displayMoney(order.delivery_fee).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_delivery_fee(orders)).split(','),
  }
];


export const defaultDriverOrdersReportFieldsConfigV2: IFieldConfig<IOrderDeliveryTask>[] =
  [
    {
      display_name: ['Order Id'],
      width_percentage: '12%',
      get_values: (attempt: IOrderDeliveryTask) => attempt.order.reference_id ? [attempt.order.order_id, attempt.order.reference_id]: [attempt.order.order_id],
      get_aggregate: (attempts: IOrderDeliveryTask[]) => [`${attempts.length} attempts`],
    },
    {
      display_name: ['Recepient'],
      width_percentage: '16%',
      get_values: (attempt: IOrderDeliveryTask) => [attempt.order.customer?.name || 'unknown'],
    },
    {
      display_name: ['Area'],
      width_percentage: '16%',
      get_values: (attempt: IOrderDeliveryTask) => [attempt.order.customer_location?.area?.name_en || 'unknown'],
    },
    {
      display_name: ['Merchant'],
      width_percentage: '16%',
      get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.order.merchant?.name || 'unknown'],
    },
    {
      display_name: ['Del. Date'],
      width_percentage: '10%',
      get_values: (attempt: IOrderDeliveryTask) => {
        if (attempt.status == OrderDeliveryTaskState.FAILED) return ['-']
        return attempt.order.delivered_at
        ? [format(new Date(attempt.order.delivered_at).toDateString(), 'dd-MM-yyyy')]
          : [];
      },
    },
    {
      display_name: ['Commission'],
      width_percentage: '10%',
      get_values: (attempt) => displayMoney(attempt.status == OrderDeliveryTaskState.COMPLETED ? attempt.driver_commission : attempt.failed_delivery_commission).split(','),
      get_aggregate: (attempts: IOrderDeliveryTask[]) => {
        return displayMoney(compute_total_driver_commission_from_delivery_tasks(attempts)).split(',')
      }
    },
    {
      display_name: ['COD'],
      width_percentage: '10%',
      get_values: (attempt: IOrderDeliveryTask) => {
        if (attempt.status == OrderDeliveryTaskState.FAILED) return ['-']
        return displayMoney(get_cod(attempt.order)).split(',')
      },
      get_aggregate: (attempts: IOrderDeliveryTask[]) =>
        displayMoney(compute_total_cod(attempts.filter(item => item.status == OrderDeliveryTaskState.COMPLETED).map(item => item.order))).split(','),
    },
    {
      display_name: ['Status'],
      width_percentage: '10%',
      get_values: (attempt: IOrderDeliveryTask) => attempt.status == OrderDeliveryTaskState.COMPLETED ? ["Delivered"] : ["Failed"]
    },
  ];


export const defaultDriverReturnReportFieldsConfigV2: IFieldConfig<IOrderDeliveryTask>[] = [
  {
    display_name: ['Order Id'],
    width_percentage: '14%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.order.order_id],
    get_aggregate: (attempts) => [`${attempts.length} attempts`],
  },
  {
    display_name: ['Ref #'],
    width_percentage: '14%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.order.reference_id],
  },
  {
    display_name: ['Customer'],
    width_percentage: '9%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.order.customer?.name || 'unknown'],
  },
  {
    display_name: ['Merchant'],
    width_percentage: '9%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.order.merchant?.name || 'unknown'],
  },
  {
    display_name: ['Commission'],
    width_percentage: '14%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => displayMoney(deliveryAttempt.status == OrderDeliveryTaskState.COMPLETED ? deliveryAttempt.driver_commission : deliveryAttempt.failed_delivery_commission).split(','),
    get_aggregate: (attempts) =>
      displayMoney(compute_total_driver_commission_from_delivery_tasks(attempts)).split(','),
  },
  {
    display_name: ['Return COD'],
    width_percentage: '14%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => displayMoney(get_cod(deliveryAttempt.order)).split(','),
    get_aggregate: (attempts) =>
      displayMoney(compute_total_cod(attempts.map(item => item.order))).split(','),
  },
  {
    display_name: ['Return Remarks'],
    width_percentage: '26%',
    get_values: (deliveryAttempt: IOrderDeliveryTask) => [deliveryAttempt.failure_reason ?? ""],
  },
  {
      display_name: ['Status'],
      width_percentage: '13%',
      get_values: (attempt: IOrderDeliveryTask) => attempt.status == OrderDeliveryTaskState.COMPLETED ? ["Delivered"] : ["Failed"]
  },
];

export const defaultDriverPickupsReportFieldsConfig: IFieldConfig<IPickupTask>[] =
  [
    {
      display_name: ['Pickup Id'],
      width_percentage: '12%',
      get_values: (task: IPickupTask) => {
        console.log('DEBUG: ', task);
        return [task.id];
      },
      get_aggregate: (tasks: IPickupTask[]) => [`${tasks.length} pickups`],
    },
    {
      display_name: ['Location'],
      width_percentage: '38%',
      get_values: (task: IPickupTask) =>
        task.workflow === Workflow.STANDARD
          ? [task.merchant_location?.area?.name_en ?? '']
          : [task.customer_location?.area?.name_en ?? ''],
    },
    {
      display_name: ['Merchant'],
      width_percentage: '20%',
      get_values: (task: IPickupTask) =>
        task.workflow === Workflow.STANDARD
          ? [task.merchant?.name ?? '']
          : [task.customer?.name ?? ''],
    },
    {
      display_name: ['Packages'],
      width_percentage: '15%',
      get_values: (task: IPickupTask) => {
        return [JSON.stringify(task.package_count)];
      },
      get_aggregate: (tasks: IPickupTask[]) => [
        tasks
          .map((task) => task.package_count)
          .reduce((prev, next) => prev + next, 0)
          .toString(),
      ],
    },
    {
      display_name: ['Commission'],
      width_percentage: '15%',
      get_values: (task: IPickupTask) =>
        displayMoney(task.driver_commission).split(','),
      get_aggregate: (tasks: IPickupTask[]) =>
        displayMoney(compute_total_pickup_commission(tasks)).split(','),
    },
    {
      display_name: ['POP'],
      width_percentage: '15%',
      get_values: (task: IPickupTask) =>
        displayMoney(add(compute_total_pop(task.orders), task.pop)).split(','),
      get_aggregate: (tasks: IPickupTask[]) => {
        const total_pop = add(
          tasks
            .map((task) => task.orders)
            .reduce(
              (acc, curr) => add(acc, compute_total_pop(curr)),
              [] as Money[]
            ),
          tasks
            .map((task) => task.pop)
            .reduce((acc, curr) => add(acc, curr), [] as Money[])
        );
        return displayMoney(total_pop).split(',');
      },
    },
  ];

export const defaultMerchantReportFieldsConfig: IFieldConfig<OrderType>[] = [
  {
    display_name: ['Order Id'],
    width_percentage: '12%',
    get_values: (order: OrderType) => order.reference_id ? [order.order_id, order.reference_id]: [order.order_id],
    get_aggregate: (orders: OrderType[]) => [`${orders.length} orders`],
  },
  {
    display_name: ['Recepient'],
    width_percentage: '16%',
    get_values: (order: OrderType) => [order.customer?.name || 'unknown'],
  },
  {
    display_name: ['Workflow'],
    width_percentage: '13%',
    get_values: (order: OrderType) => [order.workflow],
  },
  {
    display_name: ['Location'],
    width_percentage: '19%',
    get_values: (order: OrderType) => [order.customer_location?.area?.name_en || 'unknown'],
  },
  {
    display_name: ['Delivery'],
    width_percentage: '10%',
    get_values: (order) => displayMoney(order.delivery_fee).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_delivery_fee(orders)).split(','),
  },
  {
    display_name: ['POP'],
    width_percentage: '10%',
    get_values: (order: OrderType) => displayMoney(order.pop).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_pop(orders)).split(','),
  },
  {
    display_name: ['COD'],
    width_percentage: '10%',
    get_values: (order: OrderType) => displayMoney(get_cod(order)).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_cod(orders)).split(','),
  },
  {
    display_name: ['Product Price'],
    width_percentage: '10%',
    get_values: (order: OrderType) => order.product_price ? [`${order.product_price} $`] : ['X'],
    get_aggregate: (orders: OrderType[]) => [orders.filter(i => !!(i.product_price)).reduce((a,b) => Decimal(a).add(Decimal(b.product_price!)), Decimal(0)).toFixed(2)],
  }
];

export const defaultReturnReportFieldsConfig: IFieldConfig<OrderType>[] = [
  {
    display_name: ['Order Id'],
    width_percentage: '14%',
    get_values: (order: OrderType) => [order.order_id],
    get_aggregate: (orders: OrderType[]) => [`${orders.length} orders`],
  },
  {
    display_name: ['Ref #'],
    width_percentage: '14%',
    get_values: (order: OrderType) => [order.reference_id],
  },
  {
    display_name: ['Customer'],
    width_percentage: '18%',
    get_values: (order: OrderType) => [order.customer?.name || 'unknown'],
  },
  {
    display_name: ['Pay on Pickup'],
    width_percentage: '14%',
    get_values: (order: OrderType) => displayMoney(order.pop).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_pop(orders)).split(','),
  },
  {
    display_name: ['Return COD'],
    width_percentage: '14%',
    get_values: (order: OrderType) => displayMoney(get_cod(order)).split(','),
    get_aggregate: (orders: OrderType[]) =>
      displayMoney(compute_total_cod(orders)).split(','),
  },
  {
    display_name: ['Return Remarks'],
    width_percentage: '26%',
    get_values: (order: OrderType) => [order.return_reason],
  },
];

export const defaultMerchantFooter: IFooterRow<OrderType>[] = [
  {
    label: 'Net due to merchant',
    compute_value: (orders) => {
      return displayMoney(
        minus(
          compute_total_price(orders),
          orders.reduce((acc, curr) => add(curr.pop, acc), [] as Money[])
        )
      );
    },
  },
];

export const defaultMerchantReturnFooter: IFooterRow<OrderType>[] = [
  {
    label: 'Net due merchant',
    compute_value: (orders) => {
      return displayMoney(compute_total_cod(orders));
    },
  },
];

export const defaultDriverOrderFooter: IFooterRow<OrderType>[] = [
  {
    label: 'Net Due to Company',
    compute_value: (orders) => {
      const orders_excluding_already_paid_delivery = orders.filter(
        (order) => !order.delivery_fee_covered_by_merchant
      );

      let total_due_company = add(
        compute_total_price(orders),
        compute_total_delivery_fee(orders_excluding_already_paid_delivery)
      );
      total_due_company = minus(
        total_due_company,
        compute_total_driver_commission(orders)
      );

      return displayMoney(total_due_company);
    },
  },
];


export const defaultDriverOrderFooterV2: IFooterRow<IOrderDeliveryTask>[] = [
  {
    label: 'Net Due to Company',
    compute_value: (tasks: IOrderDeliveryTask[]) => {
      
      let total_due_company = compute_total_cod(tasks.filter(task => task.status == OrderDeliveryTaskState.COMPLETED).map(item=>item.order))
      
      total_due_company = minus(
        total_due_company,
        compute_total_driver_commission_from_delivery_tasks(tasks)
      );

      return displayMoney(total_due_company);
    },
  },
];

export const defaultDriverReturnOrderFooterV2: IFooterRow<IOrderDeliveryTask>[] = [
  {
    label: 'Total Commission',
    compute_value: (tasks: IOrderDeliveryTask[]) => {
      return displayMoney(compute_total_driver_commission_from_delivery_tasks(tasks));
    },
  },
];


export const defaultDriverPickupFooter: IFooterRow<IPickupTask>[] = [
  {
    label: 'Total Driver Commission',
    compute_value: (tasks) => {
      const total_commission = compute_total_pickup_commission(tasks);
      const total_pop = add(
        tasks
          .map((task) => task.orders)
          .reduce(
            (acc, curr) => add(acc, compute_total_pop(curr)),
            [] as Money[]
          ),
        tasks
          .map((task) => task.pop)
          .reduce((acc, curr) => add(acc, curr), [] as Money[])
      );

      return displayMoney(add(total_commission, total_pop));
    },
  },
];
